import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { QUALITATIVE_COLORS } from '../ids-axis-chart/ids-chart-colors';
import {
  stringToBool,
  stringToNumber,
  kebabCase
} from '../../utils/ids-string-utils/ids-string-utils';

import IdsChartLegendMixin from '../../mixins/ids-chart-legend-mixin/ids-chart-legend-mixin';
import IdsChartSelectionMixin from '../../mixins/ids-chart-selection-mixin/ids-chart-selection-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';

import IdsDataSource from '../../core/ids-data-source';

import '../ids-tooltip/ids-tooltip';
import '../ids-empty-message/ids-empty-message';

import styles from './ids-radar-chart.scss';
import type IdsEmptyMessage from '../ids-empty-message/ids-empty-message';
import type IdsTooltip from '../ids-tooltip/ids-tooltip';
import { type IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';

export type IdsRadarChartDataPoint = {
  name?: string;
  shortName?: string;
  abbrName?: string;
  value?: number;
  tooltip?: string;
};

export type IdsRadarChartSeries = {
  name?: string;
  data?: IdsRadarChartDataPoint[];
  color?: IdsColorValue;
  selected?: boolean;
  attributes?: any;
};

export type IdsRadarChartData = IdsRadarChartSeries[];

const Base = IdsChartLegendMixin(
  IdsChartSelectionMixin(
    IdsLocaleMixin(
      IdsEventsMixin(
        IdsElement
      )
    )
  )
);

/**
 * IDS Radar Chart Component
 * @type {IdsRadarChart}
 * @inherits IdsElement
 * @mixes IdsChartLegendMixin
 * @mixes IdsChartSelectionMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsEventsMixin
 * @part container - the outside container element
 * @part chart - the svg outer element
 */
@customElement('ids-radar-chart')
@scss(styles)
export default class IdsRadarChart extends Base {
  initialized = false;

  constructor() {
    super();

    // Setup default values
    if (!this.state) this.state = {};
    this.DEFAULT_SELECTABLE = true;
  }

  svg?: SVGElement | null;

  svgContainer?: HTMLElement | null;

  emptyMessage?: IdsEmptyMessage | null;

  legend?: HTMLSlotElement | null;

  /** Reference to datasource API */
  datasource = new IdsDataSource();

  /**
   * @returns {Array<string>} Radar vetoable events
   */
  vetoableEventTypes = [
    'beforeselected',
    'beforedeselected'
  ];

  /**
   * Invoked each time the custom element is connected to the DOM.
   */
  connectedCallback(): void {
    super.connectedCallback?.();

    if (!this.hasAttribute(attributes.LEGEND_PLACEMENT)) {
      this.setAttribute(attributes.LEGEND_PLACEMENT, 'right');
    }

    this.svg = this.shadowRoot?.querySelector('svg');
    this.svgContainer = this.shadowRoot?.querySelector('.ids-chart-svg-container');
    this.emptyMessage = this.querySelector('ids-empty-message') || this.shadowRoot?.querySelector('ids-empty-message');
    this.legend = this.shadowRoot?.querySelector('[name="legend"]');

    this.initialized = true;
    this.redraw();
    this.legendsClickable?.(this.selectable);
  }

  /**
   * On selectable change
   */
  onSelectableChange(): void {
    this.legendsClickable?.(this.selectable);
  }

  /**
   * Invoked when redrawing the chart
   * @private
   */
  redraw(): void {
    if (!this.initialized) {
      return;
    }

    if (!this.data || this.data.length === 0) {
      this.#showEmptyMessage();
      return;
    }

    this.#hideEmptyMessage();
    this.#addColorVariables();
    if (this.legend) this.legend.innerHTML = this.legendTemplate();
    if (this.svg) this.svg.innerHTML = this.chartTemplate();
    this.#attachTooltipEvents();
    this.#attachLegendEvents();
    this.#attachChartEvents();
    this.#preSelected();

    // Completed Event and Callback
    this.triggerEvent('rendered', this, {
      detail: {
        svg: this.svg,
        data: this.data
      }
    });
  }

  /**
   * Return the attributes handled as getters/setters
   * @returns {Array} the list of attributes
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.DATA,
      attributes.HEIGHT,
      attributes.LEVELS,
      attributes.MAX_VALUE,
      attributes.LABEL_FACTOR,
      attributes.WRAP_WIDTH,
      attributes.OPACITY_AREA,
      attributes.DOT_RADIUS,
      attributes.OPACITY_CIRCLES,
      attributes.STROKE_WIDTH,
      attributes.ROUND_STROKES,
      attributes.SHOW_CROSSLINES,
      attributes.SHOW_AXIS_LABELS,
      attributes.AXIS_FORMATTER,
      attributes.SUPPRESS_TOOLTIPS,
      attributes.TITLE,
      attributes.WIDTH
    ];
  }

  /**
   * Create the template chart
   * @returns {string} The template
   */
  template(): string {
    return `<div class="ids-chart-container" part="container">
      <div class="ids-chart-svg-container">
        <svg class="ids-radar-chart" part="chart"${this.width ? ` width="${this.width}"` : ''}${this.height ? ` height="${this.height}"` : ''} xmlns="http://www.w3.org/2000/svg">
        </svg>
      </div>
      <slot name="legend">
      </slot>
      <slot name="empty-message">
        <ids-empty-message icon="empty-no-data" hidden>
          <ids-text type="h2" font-size="20" label="true" slot="label">${this.localeAPI?.translate('NoData') || 'No data available'}</ids-text>
        </ids-empty-message>
      </slot>
      <slot name="tooltip">
        <ids-tooltip id="tooltip"></ids-tooltip>
      </slot>
    </div>`;
  }

  // Respond to changing locale
  onLocaleChange = () => {
    this.redraw();
    const textElem = this.shadowRoot?.querySelector('ids-empty-message ids-text');
    if (textElem) textElem.textContent = this.localeAPI?.translate('NoData');
  };

  // Respond to changing language
  onLanguageChange = () => {
    const textElem = this.shadowRoot?.querySelector('ids-empty-message ids-text');
    if (textElem) textElem.textContent = this.localeAPI?.translate('NoData');
  };

  /**
   * Calculate the chart markup and return it
   * @returns {string} The chart markup.
   */
  chartTemplate(): string {
    if (!this.data || this.data.length === 0) {
      return '';
    }

    const dimensions = this.#calculateDimensions();
    const { centerX, centerY, radius } = dimensions;

    const maxValue = this.#getMaxValue();
    const allAxes = this.#getAllAxes();
    const total = allAxes.length;
    const angleSlice = (Math.PI * 2) / total;

    let svg = `<g transform="translate(${centerX}, ${centerY})">`;

    // Add background circles and axis labels
    svg += this.#renderBackground(radius, maxValue);

    // Add axes
    svg += this.#renderAxes(allAxes, radius, maxValue, angleSlice);

    // Add data series
    svg += this.#renderDataSeries(angleSlice, radius, maxValue);

    svg += '</g>';

    return svg;
  }

  /**
   * Calculate chart dimensions
   * @private
   * @returns {object} Dimensions object
   */
  #calculateDimensions() {
    const containerWidth = this.width ? this.width : (this.svgContainer?.clientWidth || 400);
    const containerHeight = this.height ? this.height : (this.svgContainer?.clientHeight || 400);

    let chartWidth: number | any = containerWidth;
    const chartHeight: number | any = containerHeight;

    if (this.legendPlacement === 'right') {
      chartWidth *= 0.75;
    }

    const centerX = chartWidth / 2;
    const centerY = chartHeight / 2;
    const radius = Math.min(chartWidth, chartHeight) / 3;

    // Update SVG viewBox
    if (this.svg) {
      this.svg.setAttribute('viewBox', `0 0 ${containerWidth} ${containerHeight}`);
    }

    return {
      width: chartWidth,
      height: chartHeight,
      centerX,
      centerY,
      radius
    };
  }

  /**
   * Get maximum value from data
   * @private
   * @returns {number} Maximum value
   */
  #getMaxValue(): number {
    if (this.maxValue && this.maxValue > 0) {
      return this.maxValue;
    }

    let max = 0;
    this.data?.forEach((series: IdsRadarChartSeries) => {
      series.data?.forEach((point: IdsRadarChartDataPoint) => {
        if ((point.value || 0) > max) {
          max = point.value || 0;
        }
      });
    });

    return max || 100;
  }

  /**
   * Get all axis names
   * @private
   * @returns {string[]} Array of axis names
   */
  #getAllAxes(): string[] {
    if (!this.data || this.data.length === 0) {
      return [];
    }

    const firstSeries = this.data[0];
    return firstSeries.data?.map((point: IdsRadarChartDataPoint) => this.#getNameText(point)) || [];
  }

  /**
   * Get appropriate name text based on available space
   * @private
   * @param {IdsRadarChartDataPoint} point Data point
   * @returns {string} Name text
   */
  #getNameText(point: IdsRadarChartDataPoint): string {
    const containerWidth = this.svgContainer?.clientWidth || 400;

    if (containerWidth <= 328) {
      return point.shortName || point.abbrName || point.name || '';
    }

    if (containerWidth <= 469) {
      return point.abbrName || point.name || '';
    }

    return point.name || '';
  }

  /**
   * Render background circles and axis labels
   * @private
   * @param {number} radius Chart radius
   * @param {number} maxValue Maximum value
   * @returns {string} SVG markup
   */
  #renderBackground(radius: number, maxValue: number): string {
    let svg = '<g class="chart-radar-axis-wrapper">';

    // Background circles
    for (let i = 1; i <= this.levels; i++) {
      const r = (radius / this.levels) * i;
      svg += `<circle class="chart-radar-grid-circle" r="${r}" fill="none" stroke="var(--ids-color-border-default)" stroke-width="1" opacity="${this.opacityCircles}"></circle>`;
    }

    // Axis labels
    if (this.showAxisLabels) {
      for (let i = 1; i <= this.levels; i++) {
        const y = -(i * radius) / this.levels;
        const value = (maxValue * i) / this.levels;
        const text = this.#formatAxisValue(value);

        svg += `<text class="axis-label" x="4" y="${y}" dy="0.4em" font-size="12" fill="var(--ids-color-text-default)">${text}</text>`;
      }
    }

    svg += '</g>';
    return svg;
  }

  /**
   * Format axis value
   * @private
   * @param {number} value Value to format
   * @returns {string} Formatted value
   */
  #formatAxisValue(value: number): string {
    if (this.axisFormatter.includes('%')) {
      return `${Math.round(value * 100)}%`;
    }
    return Math.round(value).toString();
  }

  /**
   * Render axes lines and labels
   * @private
   * @param {string[]} allAxes Axis names
   * @param {number} radius Chart radius
   * @param {number} maxValue Maximum value
   * @param {number} angleSlice Angle per slice
   * @returns {string} SVG markup
   */
  #renderAxes(allAxes: string[], radius: number, maxValue: number, angleSlice: number): string {
    let svg = '';

    allAxes.forEach((axisName, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x2 = (radius * 1.05) * Math.cos(angle);
      const y2 = (radius * 1.05) * Math.sin(angle);

      // Crosslines
      if (this.showCrosslines) {
        svg += `<line class="chart-radar-crossline" x1="0" y1="0" x2="${x2}" y2="${y2}" stroke="var(--ids-color-border-default)" stroke-width="1"></line>`;
      }

      // Axis labels
      const labelX = (radius * this.labelFactor) * Math.cos(angle);
      const labelY = (radius * this.labelFactor) * Math.sin(angle);

      svg += `<text class="legend" x="${labelX}" y="${labelY}" text-anchor="middle" dy="0.35em" font-size="14" fill="var(--ids-color-text-default)">${axisName}</text>`;
    });

    return svg;
  }

  /**
   * Render data series
   * @private
   * @param {number} angleSlice Angle per slice
   * @param {number} radius Chart radius
   * @param {number} maxValue Maximum value
   * @returns {string} SVG markup
   */
  #renderDataSeries(angleSlice: number, radius: number, maxValue: number): string {
    let svg = '';
    let invisibleCirclesSvg = '';

    this.data?.forEach((series: IdsRadarChartSeries, seriesIndex: number) => {
      const color = series.color || `var(${QUALITATIVE_COLORS[seriesIndex % QUALITATIVE_COLORS.length]})`;

      // Create path data
      let pathData = '';
      const points: { x: number; y: number; value: number }[] = [];

      series.data?.forEach((point: IdsRadarChartDataPoint, pointIndex: number) => {
        const angle = angleSlice * pointIndex - Math.PI / 2;
        const value = point.value || 0;
        const r = (value / maxValue) * radius;
        const x = r * Math.cos(angle);
        const y = r * Math.sin(angle);

        points.push({ x, y, value });

        if (pointIndex === 0) {
          pathData += `M ${x} ${y}`;
        } else {
          pathData += ` L ${x} ${y}`;
        }
      });

      pathData += ' Z'; // Close the path

      // Area
      svg += `<path class="chart-radar-area" d="${pathData}" fill="${color}" fill-opacity="${this.opacityArea}" stroke="${color}" stroke-width="${this.strokeWidth}" data-series-index="${seriesIndex}"></path>`;

      // Dots
      points.forEach((point, pointIndex) => {
        svg += `<circle class="chart-radar-circle" cx="${point.x}" cy="${point.y}" r="${this.dotRadius}" fill="${color}" fill-opacity="0.8" data-series-index="${seriesIndex}" data-point-index="${pointIndex}" data-value="${point.value}"></circle>`;
      });

      // Collect invisible circles to render at the end
      points.forEach((point, pointIndex) => {
        invisibleCirclesSvg += `<circle class="radar-invisible-circle" cx="${point.x}" cy="${point.y}" r="${this.dotRadius * 1.5}" fill="none" data-series-index="${seriesIndex}" data-point-index="${pointIndex}" data-value="${point.value}"></circle>`;
      });
    });

    // Render all invisible circles at the end to ensure they're on top
    svg += invisibleCirclesSvg;

    return svg;
  }

  /**
   * Calculate the legend markup and return it
   * @returns {string} The legend markup.
   */
  legendTemplate(): string {
    if (!this.showLegend || !this.data) {
      return '';
    }

    let legend = '<div class="chart-legend">';

    this.data.forEach((series: IdsRadarChartSeries, index: number) => {
      const colorClass = ` color-${index + 1}`;

      legend += `<a href="#legend-${kebabCase(series.name || index.toString())}" data-index="${index}" class="chart-legend-item">
        <div class="swatch${colorClass}"></div>
        ${series.name || `Series ${index + 1}`}
        </a>`;
    });

    legend += '</div>';
    return legend;
  }

  /**
   * Attach tooltip events
   * @private
   */
  #attachTooltipEvents(): void {
    if (!this.suppressTooltips && this.svg) {
      const invisibleCircles = this.svg.querySelectorAll('.radar-invisible-circle');

      invisibleCircles.forEach((circle) => {
        circle.addEventListener('mouseenter', (event) => {
          this.#showTooltip(event as MouseEvent);
        });

        circle.addEventListener('mouseleave', () => {
          this.#hideTooltip();
        });
      });
    }
  }

  /**
   * Show tooltip
   * @private
   * @param {MouseEvent} event Mouse event
   */
  #showTooltip(event: MouseEvent): void {
    const target = event.target as SVGElement;
    const seriesIndex = parseInt(target.getAttribute('data-series-index') || '0', 10);
    const pointIndex = parseInt(target.getAttribute('data-point-index') || '0', 10);
    const value = parseFloat(target.getAttribute('data-value') || '0');

    const series = this.data?.[seriesIndex];
    const point = series?.data?.[pointIndex];

    if (!series || !point) return;

    const tooltip = this.shadowRoot?.querySelector('ids-tooltip') as IdsTooltip;
    if (tooltip) {
      const content = point.tooltip || `${series.name}: ${point.name} (${value})`;
      tooltip.textContent = content;
      tooltip.placement = 'top';
      tooltip.visible = true;
      if (tooltip.popup) tooltip.popup.alignTarget = target;
    }
  }

  /**
   * Hide tooltip
   * @private
   */
  #hideTooltip(): void {
    const tooltip = this.shadowRoot?.querySelector('ids-tooltip') as IdsTooltip;
    if (tooltip) {
      tooltip.visible = false;
    }
  }

  /**
   * Show empty message
   * @private
   */
  #showEmptyMessage(): void {
    if (this.emptyMessage) {
      this.emptyMessage.hidden = false;
    }
    if (this.svg) {
      this.svg.innerHTML = '';
    }
  }

  /**
   * Hide empty message
   * @private
   */
  #hideEmptyMessage(): void {
    if (this.emptyMessage) {
      this.emptyMessage.hidden = true;
    }
  }

  /**
   * Add color variables to the component using adoptedStyleSheets for consistency
   * @private
   */
  #addColorVariables(): void {
    if (!this.data || !this.shadowRoot) return;

    let colorSheet = '';
    this.data.forEach((series: IdsRadarChartSeries, index: number) => {
      const color = series.color || `var(${QUALITATIVE_COLORS[index % QUALITATIVE_COLORS.length]})`;
      colorSheet += `--ids-chart-color-${index + 1}: ${color};`;
    });

    // Create a separate stylesheet for dynamic color variables
    const variablesSheet = new CSSStyleSheet();
    variablesSheet.replaceSync(`:host { ${colorSheet} }`);

    // Add to existing adoptedStyleSheets
    const currentSheets = this.shadowRoot.adoptedStyleSheets || [];
    this.shadowRoot.adoptedStyleSheets = [...currentSheets, variablesSheet];
  }

  /**
   * Handle pre-selected items
   * @private
   */
  #preSelected(): void {
    if (!this.data || !this.svg) return;

    this.data.forEach((series: IdsRadarChartSeries, index: number) => {
      if (series.selected) {
        const area = this.svg?.querySelector(`[data-series-index="${index}"]`);
        if (area) {
          area.classList.add('is-selected');
        }
      }
    });
  }

  /**
   * Set the chart data
   * @param {IdsRadarChartData} value The data array
   */
  set data(value: IdsRadarChartData) {
    if (value) {
      this.datasource.data = value;
      this.redraw();
    }
  }

  get data(): IdsRadarChartData {
    return this.datasource?.data || [];
  }

  /**
   * Set the chart width
   * @param {string|number} value The width value
   */
  set width(value: string | number | null) {
    if (value) {
      this.setAttribute(attributes.WIDTH, String(value));
      this.svg?.setAttribute(attributes.WIDTH, String(value));
      this.redraw(); // Trigger recalculation
    } else {
      this.removeAttribute(attributes.WIDTH);
      this.svg?.removeAttribute(attributes.WIDTH);
      this.redraw();
    }
  }

  get width() {
    const attrib = this.getAttribute(attributes.WIDTH);
    return attrib ? parseFloat(attrib) : '';
  }

  /**
   * Set the chart height
   * @param {string|number} value The height value
   */
  set height(value: string | number | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, String(value));
      this.svg?.setAttribute(attributes.HEIGHT, String(value));
      this.redraw();
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.svg?.removeAttribute(attributes.HEIGHT);
      this.redraw();
    }
  }

  get height() {
    const attrib = this.getAttribute(attributes.HEIGHT);
    return attrib ? parseFloat(attrib) : '';
  }

  /**
   * Set the number of levels (concentric circles)
   * @param {number} value The number of levels
   */
  set levels(value: number) {
    if (value) {
      this.setAttribute(attributes.LEVELS, String(value));
    } else {
      this.removeAttribute(attributes.LEVELS);
    }
  }

  get levels(): number {
    return stringToNumber(this.getAttribute(attributes.LEVELS)) || 4;
  }

  /**
   * Set the maximum value
   * @param {number} value The maximum value
   */
  set maxValue(value: number) {
    if (value) {
      this.setAttribute(attributes.MAX_VALUE, String(value));
    } else {
      this.removeAttribute(attributes.MAX_VALUE);
    }
  }

  get maxValue(): number {
    return stringToNumber(this.getAttribute(attributes.MAX_VALUE)) || 0;
  }

  /**
   * Set the label factor (distance from center)
   * @param {number} value The label factor
   */
  set labelFactor(value: number) {
    if (value) {
      this.setAttribute(attributes.LABEL_FACTOR, String(value));
    } else {
      this.removeAttribute(attributes.LABEL_FACTOR);
    }
  }

  get labelFactor(): number {
    return stringToNumber(this.getAttribute(attributes.LABEL_FACTOR)) || 1.27;
  }

  /**
   * Set the wrap width for labels
   * @param {number} value The wrap width
   */
  set wrapWidth(value: number) {
    if (value) {
      this.setAttribute(attributes.WRAP_WIDTH, String(value));
    } else {
      this.removeAttribute(attributes.WRAP_WIDTH);
    }
  }

  get wrapWidth(): number {
    return stringToNumber(this.getAttribute(attributes.WRAP_WIDTH)) || 60;
  }

  /**
   * Set the area opacity
   * @param {number} value The opacity value
   */
  set opacityArea(value: number) {
    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.OPACITY_AREA, String(value));
    } else {
      this.removeAttribute(attributes.OPACITY_AREA);
    }
  }

  get opacityArea(): number {
    return stringToNumber(this.getAttribute(attributes.OPACITY_AREA)) || 0.2;
  }

  /**
   * Set the dot radius
   * @param {number} value The dot radius
   */
  set dotRadius(value: number) {
    if (value) {
      this.setAttribute(attributes.DOT_RADIUS, String(value));
    } else {
      this.removeAttribute(attributes.DOT_RADIUS);
    }
  }

  get dotRadius(): number {
    return stringToNumber(this.getAttribute(attributes.DOT_RADIUS)) || 3;
  }

  /**
   * Set the circle opacity
   * @param {number} value The opacity value
   */
  set opacityCircles(value: number) {
    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.OPACITY_CIRCLES, String(value));
    } else {
      this.removeAttribute(attributes.OPACITY_CIRCLES);
    }
  }

  get opacityCircles(): number {
    return stringToNumber(this.getAttribute(attributes.OPACITY_CIRCLES)) || 0;
  }

  /**
   * Set the stroke width
   * @param {number} value The stroke width
   */
  set strokeWidth(value: number) {
    if (value) {
      this.setAttribute(attributes.STROKE_WIDTH, String(value));
    } else {
      this.removeAttribute(attributes.STROKE_WIDTH);
    }
  }

  get strokeWidth(): number {
    return stringToNumber(this.getAttribute(attributes.STROKE_WIDTH)) || 1;
  }

  /**
   * Set whether to use round strokes
   * @param {boolean} value True for round strokes
   */
  set roundStrokes(value: boolean) {
    if (value) {
      this.setAttribute(attributes.ROUND_STROKES, String(value));
    } else {
      this.removeAttribute(attributes.ROUND_STROKES);
    }
  }

  get roundStrokes(): boolean {
    return stringToBool(this.getAttribute(attributes.ROUND_STROKES)) ?? true;
  }

  /**
   * Set whether to show crosslines
   * @param {boolean} value True to show crosslines
   */
  set showCrosslines(value: boolean) {
    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.SHOW_CROSSLINES, String(value));
    } else {
      this.removeAttribute(attributes.SHOW_CROSSLINES);
    }
  }

  get showCrosslines(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CROSSLINES)) ?? true;
  }

  /**
   * Set whether to show axis labels
   * @param {boolean} value True to show axis labels
   */
  set showAxisLabels(value: boolean) {
    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.SHOW_AXIS_LABELS, String(value));
    } else {
      this.removeAttribute(attributes.SHOW_AXIS_LABELS);
    }
  }

  get showAxisLabels(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_AXIS_LABELS)) ?? true;
  }

  /**
   * Set the axis formatter
   * @param {string} value The formatter string
   */
  set axisFormatter(value: string) {
    if (value) {
      this.setAttribute(attributes.AXIS_FORMATTER, value);
    } else {
      this.removeAttribute(attributes.AXIS_FORMATTER);
    }
  }

  get axisFormatter(): string {
    return this.getAttribute(attributes.AXIS_FORMATTER) || '.0%';
  }

  /**
   * Set whether to suppress tooltips
   * @param {boolean} value True to suppress tooltips
   */
  set suppressTooltips(value: boolean) {
    if (value) {
      this.setAttribute(attributes.SUPPRESS_TOOLTIPS, String(value));
    } else {
      this.removeAttribute(attributes.SUPPRESS_TOOLTIPS);
    }
  }

  get suppressTooltips(): boolean {
    return stringToBool(this.getAttribute(attributes.SUPPRESS_TOOLTIPS)) ?? false;
  }

  /**
   * Set the chart title element for accessibility
   * @param {string} value The title value
   */
  set title(value: string) {
    if (value) {
      this.setAttribute('aria-label', value);
      this.removeAttribute('title');
    }
  }

  get title(): string {
    return this.getAttribute('aria-label') || '';
  }

  /**
   * Set whether to show the legend
   * @param {boolean} value True to show legend
   */
  set showLegend(value: boolean) {
    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.SHOW_LEGEND, String(value));
    } else {
      this.removeAttribute(attributes.SHOW_LEGEND);
    }
  }

  get showLegend(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_LEGEND)) ?? true;
  }

  /**
   * Attach legend click events
   * @private
   */
  #attachLegendEvents(): void {
    if (!this.showLegend || !this.selectable) return;

    const legendItems = this.shadowRoot?.querySelectorAll('.chart-legend-item');

    legendItems?.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();

        const dataIndex = parseInt((item as HTMLElement).getAttribute('data-index') || '0', 10);

        // Toggle selection using the chart's selection API
        this.setSelected({ index: dataIndex }, true);

        // Update legend appearance
        this.#updateLegendAppearance();
      });
    });
  }

  /**
   * Attach chart area click events
   * @private
   */
  #attachChartEvents(): void {
    if (!this.selectable || !this.svg) return;

    const areas = this.svg.querySelectorAll('.chart-radar-area');

    areas.forEach((area, index) => {
      area.addEventListener('click', (e) => {
        e.preventDefault();

        // Toggle selection
        this.setSelected({ index }, true);
      });
    });
  }

  /**
   * Update legend item appearance based on selection state
   * @private
   */
  #updateLegendAppearance(): void {
    const legendItems = this.shadowRoot?.querySelectorAll('.chart-legend-item');
    const selectedSeries: boolean | any = this.getSelected();

    legendItems?.forEach((item, index) => {
      const isSelected: boolean | any = selectedSeries && selectedSeries.index === index;
      item.classList.toggle('selected', isSelected);
      item.classList.toggle('not-selected', selectedSeries && !isSelected);
    });
  }

  /**
   * Set selected series
   * @param {object} options Selection options
   * @param {number | any} options.index Index of the series to select
   * @param {boolean} isToggle Whether to toggle selection
   */
  setSelected(options: { index?: number }, isToggle = false) {
    if (!this.data || !this.svg) return;

    const { index } = options;
    if (typeof index !== 'number' || index < 0 || index >= this.data.length) return;

    const areas = this.svg.querySelectorAll('.chart-radar-area');
    const selectedArea = areas[index] as SVGElement;

    if (!selectedArea) return;

    const isCurrentlySelected = selectedArea.classList.contains('is-selected');

    // Clear all selections first
    areas.forEach((area: Element | any) => {
      area.classList.remove('is-selected', 'is-not-selected');
    });

    this.data.forEach((series: number | any) => {
      series.selected = false;
    });

    // If toggling and was already selected, deselect
    if (isToggle && isCurrentlySelected) {
      this.triggerEvent('deselected', this, {
        detail: {
          data: this.data[index],
          index,
          elem: selectedArea
        }
      });
      this.#updateLegendAppearance();
      return;
    }

    // Select the new series
    areas.forEach((area, i) => {
      if (i === index) {
        area.classList.add('is-selected');
      } else {
        area.classList.add('is-not-selected');
      }
    });

    this.data[index].selected = true;

    this.triggerEvent('selected', this, {
      detail: {
        data: this.data[index],
        index,
        elem: selectedArea
      }
    });

    this.#updateLegendAppearance();
  }

  /**
   * Get currently selected series
   * @returns {object|null} Selected series info
   */
  getSelected(): { data: any; index: number; elem: SVGElement } | null {
    if (!this.data || !this.svg) return null;

    const selectedIndex = this.data.findIndex((series: number | any) => series.selected);
    if (selectedIndex === -1) return null;

    const selectedArea = this.svg.querySelector('.chart-radar-area.is-selected') as SVGElement;

    return {
      data: this.data[selectedIndex],
      index: selectedIndex,
      elem: selectedArea
    };
  }
}
